// START GENAI
import { Component, h, Prop, Event, EventEmitter, State, Element, Listen } from '@stencil/core';

@Component({
  tag: 'vsr-tabs',
  styleUrl: 'vsr-tabs.scss',
  shadow: false,
})
export class VsrTabs {
  @Element() host: HTMLElement;

  @Prop() tabs: string[] = [];
  @Prop() activeTabIndex: number = 0;
  @Prop() initialTabVisible: number = 3;
  @Prop() showCloseButton: boolean = false;
  @Prop() tabStartIndex: number = 0;
  @Prop() theme: 'light' | 'dark' = 'light';
  @Prop() controlsId: string = '';

  @Event() tabClicked: EventEmitter<number>;
  @Event() tabToggleClicked: EventEmitter<void>;

  @State() isMobileView: boolean = false;
  @State() visibleTabsCount: number;
  @State() tabBarMaxWidth: string = 'none';
  @State() tabsExceedWidth: boolean = false;

  tabBarRef?: HTMLDivElement;
  tabBarContainerRef?: HTMLDivElement;

  constructor() {
    this.handleResize = this.handleResize.bind(this);
  }

  componentWillLoad() {
    this.visibleTabsCount = this.initialTabVisible;
    this.checkViewport();
  }

  componentDidLoad() {
    window.addEventListener('resize', this.handleResize);
    this.setTabBarMaxWidth();
    this.checkIfTabsExceedWidth();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  componentDidUpdate() {
    this.setTabBarMaxWidth();
    this.checkIfTabsExceedWidth();
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.checkViewport();
    this.checkIfTabsExceedWidth();
    // On mobile, always show all tabs, so reset visible count
    if (window.innerWidth < 768) {
      this.visibleTabsCount = this.tabs.length;
    } else if (this.visibleTabsCount > this.tabs.length) {
      this.visibleTabsCount = this.tabs.length;
    }
  }

  private checkViewport() {
    this.isMobileView = window.innerWidth < 768;
    if (this.isMobileView) {
      this.visibleTabsCount = this.tabs.length;
    } else if (this.visibleTabsCount === undefined || this.visibleTabsCount > this.tabs.length) {
      this.visibleTabsCount = Math.min(this.initialTabVisible, this.tabs.length);
    }
  }

  private setTabBarMaxWidth() {
    // Animate max-width only on desktop
    if (!this.isMobileView && this.tabBarRef) {
      // Force layout so scrollWidth is correct after tabs update
      window.requestAnimationFrame(() => {
        this.tabBarMaxWidth = this.tabBarRef ? `${this.tabBarRef.scrollWidth}px` : 'none';
      });
    } else {
      this.tabBarMaxWidth = 'none';
    }
  }

  private checkIfTabsExceedWidth() {
    if (this.tabBarRef && this.tabBarContainerRef) {
      // Compare the scrollWidth of the tab bar with the clientWidth of the container
      this.tabsExceedWidth = this.tabBarRef.scrollWidth > this.tabBarContainerRef.clientWidth;
    }
  }

  private onTabClick(idx: number) {
    this.tabClicked.emit(idx);
  }

  private onToggleClick() {
    if (this.visibleTabsCount < this.tabs.length) {
      // Expanding - show ALL tabs
      this.tabBarRef?.classList.add('animating', 'expanding');

      // Update the visible tabs count to show ALL tabs
      this.visibleTabsCount = this.tabs.length;

      // Remove animation classes after animation completes
      setTimeout(() => {
        this.tabBarRef?.classList.remove('animating', 'expanding');
      }, 200);
    } else {
      // Store the current width before making any changes
      const currentWidth = this.tabBarRef?.getBoundingClientRect().width || 0;

      // Mark tabs that will be hidden with a class, but keep them in the DOM
      if (this.tabBarRef) {
        const tabElements = this.tabBarRef.querySelectorAll('.vsr-tab');
        for (let i = this.initialTabVisible; i < tabElements.length; i++) {
          (tabElements[i] as HTMLElement).classList.add('will-hide-tab');
        }
      }

      // Force layout recalculation
      this.tabBarRef?.getBoundingClientRect();

      // Set a fixed width to start the animation from
      if (this.tabBarRef) {
        this.tabBarRef.style.width = `${currentWidth}px`;

        // Calculate the target width for collapsing
        const tabsToShow = this.initialTabVisible;
        const plusButton = this.tabBarRef.querySelector('.vsr-tab-next-button');
        const plusButtonWidth = plusButton ? plusButton.getBoundingClientRect().width : 0;

        // Calculate the width of visible tabs
        let visibleTabsWidth = 0;
        const tabElements = this.tabBarRef.querySelectorAll('.vsr-tab');
        for (let i = 0; i < Math.min(tabsToShow, tabElements.length); i++) {
          visibleTabsWidth += (tabElements[i] as HTMLElement).getBoundingClientRect().width;
        }

        // Add padding, gap, and button width
        const paddingAndGap = 12 * 2 + 8 * (Math.min(tabsToShow, tabElements.length) - 1);
        const targetWidth = visibleTabsWidth + paddingAndGap + plusButtonWidth;

        // Set the CSS variable for the animation
        this.tabBarRef.style.setProperty('--collapsed-width', `${targetWidth}px`);
      }

      // Add animation classes after setting the width
      setTimeout(() => {
        this.tabBarRef?.classList.add('animating', 'collapsing');

        // Wait for the animation to complete before updating the state
        setTimeout(() => {
          // Now update the visible tabs count to actually hide the tabs
          this.visibleTabsCount = this.initialTabVisible;

          // Remove animation classes
          this.tabBarRef?.classList.remove('animating', 'collapsing');

          // Remove the fixed width
          if (this.tabBarRef) {
            this.tabBarRef.style.width = '';
          }

          // Remove the will-hide-tab class
          if (this.tabBarRef) {
            const tabElements = this.tabBarRef.querySelectorAll('.will-hide-tab');
            tabElements.forEach(tab => {
              (tab as HTMLElement).classList.remove('will-hide-tab');
            });
          }
        }, 300); // Faster animation for collapsing
      }, 10); // Small delay to ensure width is set before animation starts
    }

    this.tabToggleClicked.emit();
  }

  private renderPlusIcon() {
    return (
      <svg class="vsr-tab__plus-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="7.5" y="2" width="1" height="12" fill="currentColor" />
        <rect x="2" y="7.5" width="12" height="1" fill="currentColor" />
      </svg>
    );
  }

  private renderCloseIcon() {
    return (
      <svg class="vsr-tab__close-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <line
          x1="4"
          y1="4"
          x2="12"
          y2="12"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        />
        <line
          x1="12"
          y1="4"
          x2="4"
          y2="12"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
    );
  }

  render() {
    const { tabs, tabStartIndex, activeTabIndex, theme, isMobileView } = this;

    // In mobile view, always show all tabs
    // In desktop/tablet view, show all tabs if showCloseButton is true, otherwise show limited tabs
    const displayedTabs =
      isMobileView || this.showCloseButton
        ? tabs
        : tabs.slice(tabStartIndex, tabStartIndex + this.initialTabVisible);

    const themeClass = `vsr-tab-theme-${theme}`;
    // Never show expand button on mobile
    const showExpandButton = !isMobileView && tabs.length > this.initialTabVisible;

    // Use showCloseButton prop to determine expanded state
    // Simplified class handling
    const tabBarClasses = {
      'vsr-tab-bar': true,
      expanded: this.showCloseButton,
    };

    const containerClasses = {
      'vsr-tab-bar-container': true,
      [themeClass]: true,
      'tabs-exceed-width': this.tabsExceedWidth,
    };

    return (
      <div class={containerClasses} ref={el => (this.tabBarContainerRef = el as HTMLDivElement)}>
        <div
          class={tabBarClasses}
          style={!isMobileView ? { maxWidth: this.tabBarMaxWidth } : {}}
          ref={el => (this.tabBarRef = el as HTMLDivElement)}
        >
          {displayedTabs.map((tab, idx) => (
            <button
              class={{
                'vsr-tab': true,
                active: idx === activeTabIndex,
              }}
              onClick={() => this.onTabClick(idx)}
              aria-label={tab}
              aria-controls={this.controlsId}
              type="button"
            >
              {tab}
            </button>
          ))}
          {showExpandButton && (
            <button
              class="vsr-tab-next-button"
              type="button"
              onClick={() => this.onToggleClick()}
              aria-label={this.showCloseButton ? 'close-button' : 'next-button'}
            >
              <span class="vsr-tab-next-button_icon">
                {this.showCloseButton ? this.renderCloseIcon() : this.renderPlusIcon()}
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }
}

// END GENAI
